import React, { useContext } from 'react';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import Table, { HeaderRow, TableRow } from '../../common/Widgets/Table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons/faCheck';
import { faXmark } from '@fortawesome/free-solid-svg-icons/faXmark';
import { ButtonBack } from '../../common/Button/ButtonBack';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { SiteContext } from '../../../context/SiteContext';
import { PageAbsolutePath } from '../../../consts/Common';
import Links from '../../common/Widgets/Links';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import {
  InsuranceJourneyContext,
  InsuranceJourneyState
} from '../../common/CommonTypes';
import useCarInsuranceController from '../../../hooks/insuranceJourney/useCarInsuranceController';
import { getStartQuotePath } from './InsuranceMethods';
import { useErrorBoundary } from 'react-error-boundary';

const ElectricVehiclePage = (): JSX.Element => {
  const navigate = useNavigate();
  const { showBoundary } = useErrorBoundary();

  const { site } = useContext(SiteContext);

  const { brand } = site;

  const { brandDisplayName, brandDisplayFullName, brandPhoneNumbers } = brand;

  const phoneNumber = brandPhoneNumbers.quotesAndRenewalsNumber;

  const { journey, pageEvents } = useOutletContext<InsuranceJourneyContext>();

  const { staticData, insuranceNavigationStatus } = journey;

  const { commonEvents } = pageEvents;

  const { setQuote } = commonEvents;

  const { getNewQuote } = useCarInsuranceController();

  const goBack = (): void => {
    navigate(-1);
  };

  const getNewQuoteSuccess = (quote: InsuranceJourneyState) => {
    setQuote({
      ...quote,
      tiaProductCode: site.brand.tiaProductCode,
      staticData: [...staticData],
      insuranceNavigationStatus: { ...insuranceNavigationStatus }
    });

    navigate(
      getStartQuotePath(
        site.user.isDealer ?? false,
        site.brand.useAlternateEligibility
      )
    );
  };

  const onBackendFailure = (err: string) => {
    showBoundary(new Error(err));
  };

  const onClickStartQuote = () => {
    getNewQuote(
      site.brand.brandName,
      site.user?.insuranceDealerCode ?? '',
      site.user.token,
      getNewQuoteSuccess,
      onBackendFailure
    );
  };

  const tableHeader: HeaderRow = {
    columns: [
      `${brandDisplayName} Guaranteed Benefits`,
      `${brandDisplayFullName} Insurance`,
      'Top UK Insurers'
    ]
  };

  const tickComponent = <FontAwesomeIcon icon={faCheck} />;

  const crossComponent = <FontAwesomeIcon icon={faXmark} />;

  const tableRows: TableRow[] = [
    {
      columns: [
        `${brandDisplayName} genuine parts`,
        tickComponent,
        crossComponent
      ]
    },
    {
      columns: [
        `${brandDisplayName} Approved Bodyshops`,
        tickComponent,
        crossComponent
      ]
    },
    {
      columns: [
        `${brandDisplayName} recognised accredited technicians`,
        tickComponent,
        crossComponent
      ]
    },
    {
      columns: [
        `${brandDisplayName} manufacturer warranty preserved1 by fitting ${brandDisplayName} Genuine Parts`,
        tickComponent,
        crossComponent
      ]
    }
  ];

  const chevronLeftComponent = <FontAwesomeIcon icon={faChevronLeft} />;
  const policyDocUrl = `https://www.${site.brand.brandName}carinsurance.co.uk/docs`;

  return (
    <div className='electric-vehicles'>
      <Links
        id='back-link'
        title=''
        items={[{ to: '', text: 'Back', onClick: goBack }]}
        icon={chevronLeftComponent}
      />
      <div className='electric-logo'>
        <p className='mobile-hide'>Stop wondering. Start driving</p>
      </div>
      <h2>{brandDisplayName} Electric Vehicle Insurance</h2>
      <p>
        When you own a {brandDisplayName} Battery Electric Vehicle (BEV) or
        Plug-in Hybrid Electric Vehicle (PHEV), you want to know that it's in
        the best possible hands. With {brandDisplayName} Electric Vehicle Annual
        Insurance, you can be confident that your car will receive the
        specialist electric vehicle cover and care it needs.
      </p>
      <ul>
        <li>
          Cover for your {brandDisplayName} electric vehicle charging cable and
          battery*
        </li>
        <li>Specialist electric vehicle:</li>
        <ul>
          <li>
            technicians certified to the accreditation standard recognised by
            {brandDisplayName}
          </li>
          <li>dedicated repair environment</li>
          <li>tooling and equipment</li>
        </ul>
        <li>5-year repair guarantee</li>
        <li>
          A brand new {brandDisplayName} electric vehicle if yours is written
          off or stolen within 12 months of its new registration date and not
          recovered
        </li>
        <li>24-hour UK accident recovery when you make a claim</li>
        <li>A UK based claims team available 24 hours a day</li>
        <li>
          Home charging cover is available on request. Please call
          <a className='calltext' href={`tel:${phoneNumber}`}>
            {phoneNumber}
          </a>
          for details as this cannot be added onto an online quotation.
        </li>
      </ul>
      <ButtonContinue
        name='btnStartQuote'
        text='Start Quote'
        onClick={onClickStartQuote}
        additionalClassNames='btn-start'
      />
      <h5>You’ll also receive our exclusive Repair Promise</h5>
      <Table
        id='landing-page-table'
        header={tableHeader}
        rows={tableRows}
        addendum='Research: UK motor insurers’ policy wordings, August 2024.'
      />
      <h5>That’s not all</h5>
      <p>
        Our fully comprehensive policy includes cover for windscreen damage,
        personal injury, replacement locks, personal possessions and much more.
      </p>
      <p>
        Call
        <a className='calltext' href={`tel:${phoneNumber}`}>
          {phoneNumber}
        </a>
        for a competitive quote.
      </p>
      <ButtonContinue
        name='btnStartQuote'
        text='Start Quote'
        onClick={onClickStartQuote}
        additionalClassNames='btn-start'
      />
      <p>
        The cover outlined is for {brandDisplayName} Battery Electric Vehicles
        (BEV) and {brandDisplayName}
        Plug-in Hybrid Electric Vehicles (PHEV). Insurance cover is also
        available for Self-Charging Hybrid, Mild Hybrid and Internal Combustion
        Engine (ICE) vehicles. The offer of insurance is subject to eligibility
        and underwriting criteria. All cover is subject to the terms and
        conditions provided with the policy, the
        <Link
          id='lnkPolicyDocument'
          className='in-text'
          to={PageAbsolutePath.Insurance.PolicyDocuments}>
          Policy Document
        </Link>
        and the
        <Link
          id='lnkTermsOfBusiness'
          className='in-text'
          to={PageAbsolutePath.Insurance.BusinessTermsAncillaryPage}>
          Terms of Business
        </Link>
        available at{' '}
        <a className='in-text' href={policyDocUrl} target='_blank'>
          {policyDocUrl}
        </a>
        . *Exclusions apply for leased batteries. <sup>1</sup>If your insurer
        uses non-genuine parts which subsequently fail, they won't be covered
        under your manufacturer's warranty.
      </p>
      <div className='flex-row'>
        <ButtonBack
          name='btnBack'
          text='Back'
          onClick={goBack}
          additionalClassNames='btn-back'
        />
      </div>
    </div>
  );
};

export default ElectricVehiclePage;
